import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'

type RegionPreset = { value: string; label: string }

type OcrProvider = {
  id: string
  label: string
  doc_url: string
  note: string
  fields: string[]
  region_presets: RegionPreset[]
}

type OcrConfig = {
  enabled: boolean
  default_on_upload: boolean
  provider: string
  app_id: string
  api_key: string
  api_secret: string
  region: string
  endpoint: string
  tesseract_cmd: string
  languages: string
  max_pdf_pages: number
  providers?: OcrProvider[]
}

export default function OcrSettings() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [providers, setProviders] = useState<OcrProvider[]>([])

  const enabled = Form.useWatch('enabled', form)
  const provider = Form.useWatch('provider', form) || 'tesseract'

  const providerDef = useMemo(
    () => providers.find((p) => p.id === provider),
    [providers, provider],
  )

  const loadConfig = () =>
    adminApi.get<OcrConfig>('/admin/settings/ocr').then((r) => {
      setProviders(r.data.providers || [])
      form.setFieldsValue({
        enabled: r.data.enabled ?? false,
        default_on_upload: r.data.default_on_upload ?? false,
        provider: r.data.provider || 'tesseract',
        app_id: r.data.app_id || '',
        api_key: r.data.api_key || '',
        api_secret: r.data.api_secret || '',
        region: r.data.region || '',
        endpoint: r.data.endpoint || '',
        tesseract_cmd: r.data.tesseract_cmd || '',
        languages: r.data.languages || 'chi_sim+eng',
        max_pdf_pages: r.data.max_pdf_pages ?? 15,
      })
    })

  useEffect(() => {
    loadConfig().catch((e) => message.error(e.message))
  }, [form])

  const save = () => {
    void form.validateFields().then((v) => {
      setLoading(true)
      const payload = { ...v }
      if (!payload.api_secret) delete payload.api_secret
      adminApi
        .put('/admin/settings/ocr', payload)
        .then(() => {
          message.success('OCR 配置已保存')
          return loadConfig()
        })
        .catch((e) => message.error(e.message))
        .finally(() => setLoading(false))
    })
  }

  const testConn = () => {
    setTesting(true)
    adminApi
      .post<{ ok: boolean; message: string; sample_text?: string }>('/admin/settings/ocr/test')
      .then((r) => {
        const hint = r.data.sample_text ? `样例：${r.data.sample_text.slice(0, 80)}` : ''
        message.success(`${r.data.message || '测试通过'}${hint ? `（${hint}）` : ''}`)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setTesting(false))
  }

  const providerOptions =
    providers.length > 0
      ? providers.map((p) => ({ value: p.id, label: p.label }))
      : [
          { value: 'tesseract', label: 'Tesseract（本地）' },
          { value: 'textin', label: '合合 TextIn' },
          { value: 'aliyun', label: '阿里云 OCR' },
          { value: 'tencent', label: '腾讯云 OCR' },
          { value: 'baidu', label: '百度智能云 OCR' },
          { value: 'huawei', label: '华为云 OCR' },
          { value: 'xunfei', label: '讯飞 OCR' },
          { value: 'paddleocr', label: 'PaddleOCR（本地服务）' },
        ]

  const regionOptions = (providerDef?.region_presets || []).map((r) => ({
    value: r.value,
    label: r.label,
  }))

  const showField = (name: string) => (providerDef?.fields || []).includes(name)

  return (
    <div>
      <PageHeader
        title="OCR 配置"
        description="配置 PDF/图片/文档内嵌图 OCR。启用后：上传可勾选 OCR；重新索引会对扫描 PDF、图片及 Word/PPT/Excel/PDF 内嵌图执行识别并写入切片。"
      />

      <Card>
        <Form form={form} layout="vertical" style={{ maxWidth: 720 }}>
          <Form.Item name="enabled" label="启用 OCR" valuePropName="checked">
            <Switch />
          </Form.Item>

          {enabled ? (
            <>
              <Form.Item
                name="default_on_upload"
                label="上传时默认开启 OCR"
                valuePropName="checked"
                extra="知识库上传弹窗中「PDF/图片 OCR」开关的默认状态。"
              >
                <Switch />
              </Form.Item>

              <Form.Item name="provider" label="OCR 厂商" rules={[{ required: true }]}>
                <Select options={providerOptions} />
              </Form.Item>

              {providerDef?.note ? (
                <Typography.Paragraph type="secondary" style={{ marginTop: -8, marginBottom: 16 }}>
                  {providerDef.note}
                  {providerDef.doc_url ? (
                    <>
                      {' '}
                      <a href={providerDef.doc_url} target="_blank" rel="noreferrer">
                        官方文档
                      </a>
                    </>
                  ) : null}
                </Typography.Paragraph>
              ) : null}

              {showField('tesseract_cmd') ? (
                <Form.Item
                  name="tesseract_cmd"
                  label="Tesseract 可执行文件路径"
                  extra="Windows 示例：C:\\Program Files\\Tesseract-OCR\\tesseract.exe；Linux 可留空使用 PATH。"
                >
                  <Input placeholder="留空则使用系统 PATH" autoComplete="off" />
                </Form.Item>
              ) : null}

              {showField('languages') ? (
                <Form.Item
                  name="languages"
                  label="Tesseract 语言包"
                  rules={[{ required: true }]}
                  extra="多个语言用 + 连接，如 chi_sim+eng"
                >
                  <Input placeholder="chi_sim+eng" />
                </Form.Item>
              ) : null}

              {showField('app_id') ? (
                <Form.Item
                  name="app_id"
                  label={provider === 'huawei' ? 'Project ID（项目 ID）' : 'App ID'}
                  rules={[{ required: true }]}
                >
                  <Input autoComplete="off" />
                </Form.Item>
              ) : null}

              {showField('api_key') ? (
                <Form.Item
                  name="api_key"
                  label={
                    provider === 'tencent'
                      ? 'SecretId'
                      : provider === 'baidu'
                        ? 'API Key'
                        : 'Access Key ID / API Key'
                  }
                  rules={[{ required: true }]}
                >
                  <Input autoComplete="off" />
                </Form.Item>
              ) : null}

              {showField('api_secret') ? (
                <Form.Item
                  name="api_secret"
                  label={
                    provider === 'textin'
                      ? 'Secret Code'
                      : provider === 'tencent'
                        ? 'SecretKey'
                        : provider === 'baidu'
                          ? 'Secret Key'
                          : 'Access Key Secret / APISecret'
                  }
                  rules={provider === 'textin' ? [{ required: true, message: '请填写 Secret' }] : []}
                  extra="留空保存则保持库中已有密钥不变。"
                >
                  <Input.Password autoComplete="off" />
                </Form.Item>
              ) : null}

              {showField('region') ? (
                <Form.Item name="region" label="区域" rules={[{ required: true }]}>
                  {regionOptions.length ? (
                    <Select options={regionOptions} placeholder="选择区域" allowClear={false} />
                  ) : (
                    <Input placeholder="区域标识" />
                  )}
                </Form.Item>
              ) : null}

              {showField('endpoint') ? (
                <Form.Item
                  name="endpoint"
                  label="服务地址"
                  rules={[{ required: true }]}
                  extra="PaddleOCR HTTP 服务根地址，如 http://127.0.0.1:8866"
                >
                  <Input placeholder="http://127.0.0.1:8866" />
                </Form.Item>
              ) : null}

              <Form.Item
                name="max_pdf_pages"
                label="PDF 最大 OCR 页数"
                rules={[{ required: true }]}
                extra="扫描件 PDF 仅 OCR 前 N 页，避免超大文件耗时过长。"
              >
                <InputNumber min={1} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </>
          ) : (
            <Typography.Paragraph type="secondary">
              关闭后，上传与重新索引均不会执行 OCR（即使上传弹窗勾选也会被忽略）。
            </Typography.Paragraph>
          )}

          <Space>
            <Button type="primary" loading={loading} onClick={save}>
              保存配置
            </Button>
            {enabled ? (
              <Button loading={testing} onClick={testConn}>
                测试识别
              </Button>
            ) : null}
          </Space>
        </Form>
      </Card>
    </div>
  )
}
